import { useEffect, useId, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Building2, Compass, GraduationCap, Home, LogIn, Menu, MessagesSquare, UserRound, X } from 'lucide-react';
import { Prefs } from '@/components/Prefs';
import { requestLandingTour } from '@/help/landing-tour';
import { useT } from '@/i18n/context';
import { useAuth } from '@/auth/AuthProvider';

/**
 * ترويسةُ الصفحات العلنية — **مصدرٌ واحد** (18 سبتمبر 2026).
 *
 * كانت مكتوبةً مرتين: مرةً في الصفحة الأولى ومرةً في `PublicLayout`. فافترقتا:
 * «التحقّق من شهادة» كان في إحداهما دون الأخرى، فكان الزائر لا يراه في الصفحة
 * الأولى ثم يراه فجأةً بعد أن ينقر أيَّ قسمٍ آخر — يبدو كعطلٍ وهو اختلافُ نسختين.
 *
 * فالترويسةُ الآن مكوّنٌ واحد لا نسختان: ما يُضاف هنا يظهر في كل صفحةٍ علنية،
 * وما يُحذف يُحذف منها كلِّها. هذا هو الإصلاح الحقيقي؛ ومزامنةُ القائمتين باليد
 * إصلاحٌ يعود العطلُ بعده عند أول إضافة.
 *
 * و**القسمُ الذي أنت فيه مُظلَّل** (19 سبتمبر 2026): الزائرُ ينقر «المؤسسات»
 * فتنتقل الصفحةُ ولا شيء في الترويسة يقول أين هو. والتظليلُ لونٌ وخطٌّ تحته
 * معاً لا لونٌ وحده — فمن لا يميّز الألوان يراه أيضاً.
 *
 * و**على الهاتف زرُّ قائمة** (19 سبتمبر 2026): كانت `.ld-nav` تُخفى تحت 900px
 * **بلا بديل**، فزائرُ الهاتف — وهو الأكثر — لا يرى المؤسسات ولا الدورات ولا
 * التحقّق أصلاً، ولا طريق له إليها إلا أن يعرف الرابط. والقائمةُ تُبنى من
 * `LINKS` نفسِها لا من نسخةٍ ثانية، لأن هذا بالذات هو العطلُ الذي وحّدنا
 * الترويسة من أجله.
 */

/** أقسامُ الترويسة — تُقرأ مرّتين (سطحُ الحاسوب ولوحُ الهاتف) وتُكتب مرّةً */
function useLinks() {
  const t = useT();
  return [
    ['/centers', t('landing.nav.centers')],
    ['/consultants', t('landing.nav.consult')],
    ['/academy', t('landing.nav.courses')],
    ['/jobs', t('landing.nav.jobs')],
    ['/cadres', t('landing.nav.cadres')],
    ['/news', t('public.nav.news')],
    ['/verify', t('public.nav.verify')],
  ] as const;
}

/** `NavLink` يضع `aria-current="page"` وحده — واللونُ يتبعه لا يسبقه */
const current = ({ isActive }: { isActive: boolean }) => (isActive ? 'is-current' : undefined);

export function PublicHeader() {
  // من دخل ثم فتح الموقع من شعار المنصة يجد «حسابي» لا «تسجيل دخول» (13 أكتوبر 2026)
  const { status: authStatus } = useAuth();
  const t = useT();
  const links = useLinks();
  const panelId = useId();
  const { pathname } = useLocation();
  const btnRef = useRef<HTMLButtonElement>(null);

  /*
    الانتقالُ يُغلق اللوح: من نقر قسماً وصل إليه، ولوحٌ يبقى مفتوحاً فوقه يحجبه.
    والإغلاقُ **يُشتقّ عند التصيير** لا يُكتب في تأثير — الحالةُ تحفظ المسارَ الذي
    فُتح فيه اللوح، فإن تغيّر المسار لم يعد مفتوحاً. تأثيرٌ يستدعي `setState`
    يُصيّر الصفحة مرّتين ويُظهر اللوحَ لحظةً فوق الصفحة الجديدة.
  */
  const [menu, setMenu] = useState({ open: false, at: pathname });
  const open = menu.open && menu.at === pathname;
  const setOpen = (v: boolean) => setMenu({ open: v, at: pathname });

  /* Escape يُغلق ويُعيد التركيز إلى الزرّ — لا يُترك التركيزُ في لوحٍ اختفى */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      /* `setMenu` بدل `setOpen` هنا: الإغلاقُ لا يحتاج المسار فلا يُعاد تركيبُ المستمع عند كل تنقّل */
      if (e.key === 'Escape') { setMenu((m) => ({ ...m, open: false })); btnRef.current?.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <header className={`ld-header${open ? ' ld-header--open' : ''}`}>
      <div className="wrap">
        <Link to="/" className="ld-brand" aria-label={t('app.brandAria')}>
          <img src="/brand/rafrafa-mark.webp" alt="" width={34} height={40} decoding="async" />
          <b>{t('brand.name')}</b>
        </Link>
        <nav className="ld-nav" aria-label={t('public.nav.aria')} data-tour="ld:nav">
          {links.map(([to, label]) => (
            <NavLink key={to} to={to} className={current}>{label}</NavLink>
          ))}
        </nav>
        <div className="ld-cta">
          {/* جولةُ الصفحة الرئيسية بدل «اسأل المساعد» (18 أكتوبر 2026 — غايتنا) — في الصفحة الرئيسية وحدها */}
          {pathname === '/' && (
            <button type="button" className="ld-ask" data-testid="public-tour" onClick={() => requestLandingTour()} aria-label="جولة في الصفحة" title="جولة في الصفحة"><Compass size={20} aria-hidden /><span>جولة</span></button>
          )}
          <Prefs />
          {/* بابٌ واحد يفرز الناس خلفه — لا زرّان متجاوران يظنّ الزائرُ أن أحدهما ليس له */}
          {authStatus === 'authenticated'
            ? <Link to="/home" className="btn btn--primary" data-tour="ld:signin">حسابي</Link>
            : <Link to="/login" className="btn btn--primary" data-tour="ld:signin">{t('landing.cta.signin')}</Link>}
          <button
            ref={btnRef}
            type="button"
            className="ld-burger"
            aria-expanded={open}
            aria-controls={panelId}
            aria-label={t(open ? 'public.nav.close' : 'public.nav.open')}
            onClick={() => setOpen(!open)}
          >
            {open ? <X size={20} aria-hidden /> : <Menu size={20} aria-hidden />}
          </button>
        </div>
      </div>

      {/* اللوحُ يُزال من الشجرة عند الإغلاق فلا يبقى في ترتيب التنقّل بلوحة المفاتيح */}
      {open && (
        <nav id={panelId} className="ld-menu" aria-label={t('public.nav.aria')}>
          {links.map(([to, label]) => (
            <NavLink key={to} to={to} className={current}>{label}</NavLink>
          ))}
        </nav>
      )}
    </header>
  );
}

/**
 * شريطُ التبويب السفلي للصفحات العلنية على الجوال (25 سبتمبر 2026): الزائرُ يمسك الجوال بيدٍ
 * واحدة، والأبوابُ الأربعة الأكثر طلباً تحت إبهامه — والباقي (الكوادر، الوظائف، الأخبار،
 * التحقّق) في القائمة أعلاه كما كان. والخامسُ «دخول» لمن لم يدخل و«حسابي» لمن دخل.
 */
const tab = ({ isActive }: { isActive: boolean }) => `tabbar__item${isActive ? ' is-current' : ''}`;

export function PublicTabBar() {
  const t = useT();
  const { status } = useAuth();
  const signedIn = status === 'authenticated';
  return (
    <nav className="tabbar tabbar--public" aria-label="التنقّل السريع">
      <NavLink to="/" end className={tab}><Home aria-hidden /><span>الرئيسية</span></NavLink>
      <NavLink to="/centers" className={tab}><Building2 aria-hidden /><span>{t('landing.nav.centers')}</span></NavLink>
      <NavLink to="/consultants" className={tab}><MessagesSquare aria-hidden /><span>{t('landing.nav.consult')}</span></NavLink>
      <NavLink to="/academy" className={tab}><GraduationCap aria-hidden /><span>{t('landing.nav.courses')}</span></NavLink>
      {signedIn
        ? <NavLink to="/home" className={tab}><UserRound aria-hidden /><span>حسابي</span></NavLink>
        : <NavLink to="/login" className={tab}><LogIn aria-hidden /><span>دخول</span></NavLink>}
    </nav>
  );
}
